import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { 
  Zap, Camera, Film, Clock, CheckCircle, Play, Menu, X, 
  Phone, Mail, MapPin, Instagram, Youtube, MessageCircle,
  ChevronRight, Star, Smartphone, Video, Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster, toast } from "sonner";

// Admin imports
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_dec8cbf5-12ea-4fc0-855f-362ec3363e42/artifacts/i11k2q2i_Nammareels%20logo%20IMG_0308.PNG";
const VIDEOGRAPHER_URL = "https://customer-assets.emergentagent.com/job_dec8cbf5-12ea-4fc0-855f-362ec3363e42/artifacts/uu7y87pu_ChatGPT%20Image%20Feb%2028%2C%202026%2C%2012_09_25%20AM.png";

// Navbar Component
const Navbar = ({ onBookNow, onMenuToggle, isMenuOpen }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav 
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? "bg-[#050505]/95 backdrop-blur-md border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo - Increased size */}
          <a href="#" data-testid="navbar-logo" className="flex-shrink-0 flex items-center">
            <img 
              src={LOGO_URL} 
              alt="Nammareels.in" 
              className="h-[100px] md:h-[160px] w-auto object-contain"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <a href="#why-us" className="text-white/70 hover:text-white transition-colors font-body text-sm uppercase tracking-wider">Why Us</a>
            <a href="#how-it-works" className="text-white/70 hover:text-white transition-colors font-body text-sm uppercase tracking-wider">How It Works</a>
            <a href="#pricing" className="text-white/70 hover:text-white transition-colors font-body text-sm uppercase tracking-wider">Pricing</a>
            <a href="#faq" className="text-white/70 hover:text-white transition-colors font-body text-sm uppercase tracking-wider">FAQ</a>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <Button
              data-testid="navbar-book-now-btn"
              onClick={onBookNow}
              className="bg-[#dc2626] hover:bg-[#b91c1c] text-white font-heading uppercase tracking-wider px-6 py-5 btn-primary"
            >
              Book Now
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={onMenuToggle}
            className="md:hidden p-2 text-white"
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>
    </nav>
  );
};

// Mobile Menu Component
const MobileMenu = ({ isOpen, onClose, onBookNow }) => {
  if (!isOpen) return null;

  return (
    <div 
      data-testid="mobile-menu"
      className="fixed inset-0 z-40 md:hidden"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={onClose} />
      
      {/* Menu Panel */}
      <div className="absolute right-0 top-0 h-full w-[85%] max-w-sm bg-[#0a0a0a] border-l border-white/10 overflow-hidden">
        {/* Videographer Image - Right Side Background */}
        <div className="absolute right-0 top-0 h-full w-[45%] pointer-events-none overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-l from-transparent via-[#0a0a0a]/70 to-[#0a0a0a] z-10" />
          <img 
            src={VIDEOGRAPHER_URL}
            alt="Nammareels Videographer"
            className="absolute right-0 top-1/2 -translate-y-1/2 h-[90%] w-auto object-contain object-right opacity-60"
          />
          {/* Red glow effect */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-32 h-32 bg-[#dc2626]/30 rounded-full blur-[60px]" />
        </div>

        {/* Content Side */}
        <div className="relative z-20 flex flex-col pt-24 px-6 h-full">
          <nav className="flex flex-col gap-6">
            <a 
              href="#why-us" 
              onClick={onClose}
              className="text-white text-2xl font-heading uppercase tracking-wider hover:text-[#dc2626] transition-colors"
            >
              Why Us
            </a>
            <a 
              href="#how-it-works" 
              onClick={onClose}
              className="text-white text-2xl font-heading uppercase tracking-wider hover:text-[#dc2626] transition-colors"
            >
              How It Works
            </a>
            <a 
              href="#pricing" 
              onClick={onClose}
              className="text-white text-2xl font-heading uppercase tracking-wider hover:text-[#dc2626] transition-colors"
            >
              Pricing
            </a>
            <a 
              href="#faq" 
              onClick={onClose}
              className="text-white text-2xl font-heading uppercase tracking-wider hover:text-[#dc2626] transition-colors"
            >
              FAQ
            </a>
          </nav>

          <div className="mt-auto pb-8">
            <Button
              data-testid="mobile-book-now-btn"
              onClick={() => { onBookNow(); onClose(); }}
              className="w-full bg-[#dc2626] hover:bg-[#b91c1c] text-white font-heading uppercase tracking-wider py-6 text-lg"
            >
              Book Now
            </Button>

            {/* Contact Info */}
            <div className="mt-6 space-y-3 text-white/60 text-sm">
              <p className="flex items-center gap-2">
                <MapPin size={16} />
                Bengaluru, Karnataka
              </p>
              <a href="mailto:nammareels.in@gmail.com" className="flex items-center gap-2">
                <Mail size={16} />
               <span>nammareels.in@gmail.com</span>
             </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Hero Section
const HeroSection = ({ onBookNow }) => {
  const [scrollY, setScrollY] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Calculate scroll-based transforms for "SAME DAY" text - parallax effect
  const sameDayTransform = Math.min(scrollY * 0.5, 150);
  const sameDayScale = Math.max(1 - scrollY * 0.001, 0.6);
  const sameDayOpacity = Math.max(1 - scrollY * 0.002, 0.3);

  return (
    <section data-testid="hero-section" className="relative min-h-screen overflow-hidden">
      {/* Deep Red Gradient Background */}
      <div className="absolute inset-0 bg-[#050505]" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#0a0505] to-[#050505]" />
      <div className="absolute right-0 top-0 w-[60%] h-full bg-gradient-to-l from-[#1a0808] via-[#120505] to-transparent" />
      <div className="absolute left-0 top-0 w-[60%] h-full bg-gradient-to-r from-[#1a0808] via-[#120505] to-transparent" />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#dc2626]/15 rounded-full blur-[180px]" />

      {/* BIG "SAME DAY" TEXT - Behind Everything (z-index: 1) */}
      <div 
        className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden"
        style={{ zIndex: 1 }}
      >
        <div 
          className="text-center transition-all duration-700 ease-out will-change-transform"
          style={{
            transform: `translateY(-${sameDayTransform}px) scale(${sameDayScale})`,
            opacity: sameDayOpacity,
          }}
        >
          <span 
            className="font-heading font-bold text-transparent uppercase leading-[0.75] tracking-[-0.02em] block"
            style={{
              fontSize: 'clamp(120px, 22vw, 320px)',
              WebkitTextStroke: '2px rgba(220, 38, 38, 0.15)',
              textShadow: '0 0 80px rgba(220, 38, 38, 0.1)',
            }}
          >
            SAME
          </span>
          <span 
            className="font-heading font-bold text-transparent uppercase leading-[0.75] tracking-[-0.02em] block -mt-2 md:-mt-6 lg:-mt-10"
            style={{
              fontSize: 'clamp(120px, 22vw, 320px)',
              WebkitTextStroke: '2px rgba(220, 38, 38, 0.15)',
              textShadow: '0 0 80px rgba(220, 38, 38, 0.1)',
            }}
          >
            DAY
          </span>
        </div>
      </div>

      {/* Solid Fill Layer */}
      <div 
        className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden"
        style={{ zIndex: 1 }}
      >
        <div 
          className="text-center transition-all duration-700 ease-out will-change-transform"
          style={{
            transform: `translateY(-${sameDayTransform}px) scale(${sameDayScale})`,
            opacity: sameDayOpacity * 0.8,
          }}
        >
          <span 
            className="font-heading font-bold uppercase leading-[0.75] tracking-[-0.02em] block"
            style={{
              fontSize: 'clamp(120px, 22vw, 320px)',
              color: 'rgba(220, 38, 38, 0.1)',
            }}
          >
            SAME
          </span>
          <span 
            className="font-heading font-bold uppercase leading-[0.75] tracking-[-0.02em] block -mt-2 md:-mt-6 lg:-mt-10"
            style={{
              fontSize: 'clamp(120px, 22vw, 320px)',
              color: 'rgba(220, 38, 38, 0.1)',
            }}
          >
            DAY
          </span>
        </div>
      </div>

      {/* Main Content - Centered Vertical Stack (z-index: 10) */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 md:px-8 py-24">
        
        {/* TOP: Text Content - Centered */}
        <div className="text-center mb-8 md:mb-12">
          {/* Tagline */}
          <p className="text-[#dc2626] font-heading text-sm md:text-base uppercase tracking-[0.3em] mb-4 animate-slide-up">
            Quick Content Service
          </p>

          {/* Main Headline - All on top, centered */}
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white uppercase leading-[0.95] tracking-tight animate-slide-up stagger-1">
            We Shoot <span className="text-[#dc2626]">•</span> Edit <span className="text-[#dc2626]">•</span> Deliver Reels in{" "}
            <span className="text-[#dc2626]">Same Day</span>
          </h1>
        </div>

        {/* MIDDLE: Phone Mockup - Centered */}
        <div 
          className="relative mb-8 md:mb-10 animate-slide-up stagger-2"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Red glow behind phone */}
          <div className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-[#dc2626]/25 rounded-full blur-[80px] transition-all duration-500 ${isHovered ? 'w-80 h-80 bg-[#dc2626]/40' : ''}`} />
          
          {/* Phone Mockup */}
          <div 
            className="relative transition-all duration-300 ease-out"
            style={{
              transform: isHovered 
                ? 'scale(1.02) perspective(1000px) rotateX(2deg) rotateY(-2deg)' 
                : 'scale(1)',
              boxShadow: isHovered 
                ? '0 40px 80px rgba(0,0,0,0.6), 0 0 60px rgba(220,38,38,0.25)' 
                : '0 30px 60px rgba(0,0,0,0.5)'
            }}
          >
            {/* Phone Frame */}
            <div className="relative w-[200px] h-[410px] sm:w-[230px] sm:h-[470px] md:w-[260px] md:h-[530px] bg-gradient-to-b from-[#1a1a1a] to-[#0d0d0d] rounded-[36px] sm:rounded-[40px] md:rounded-[44px] p-[3px]">
              {/* Phone Inner Border */}
              <div className="w-full h-full bg-gradient-to-b from-[#2a2a2a] to-[#1a1a1a] rounded-[33px] sm:rounded-[37px] md:rounded-[41px] p-[5px] md:p-[7px]">
                {/* Phone Screen */}
                <div className="relative w-full h-full bg-black rounded-[28px] sm:rounded-[32px] md:rounded-[34px] overflow-hidden">
                  {/* Dynamic Island */}
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 w-[65px] sm:w-[75px] md:w-[85px] h-[20px] sm:h-[23px] md:h-[26px] bg-black rounded-full z-20 flex items-center justify-center">
                    <div className="w-2 md:w-2.5 h-2 md:h-2.5 bg-[#1a1a1a] rounded-full mr-1.5" />
                    <div className="w-1.5 h-1.5 bg-[#0a3d0a] rounded-full" />
                  </div>
                  
                  {/* Screen Content */}
                  <div className="absolute inset-0">
                    <img 
                      src={VIDEOGRAPHER_URL}
                      alt="Nammareels Professional Videographer"
                      className="w-full h-full object-cover object-center"
                    />
                    <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/80 to-transparent" />
                    
                    {/* Play button */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#dc2626]/90 rounded-full flex items-center justify-center shadow-lg shadow-[#dc2626]/40 hover:scale-110 transition-transform cursor-pointer">
                        <Play size={20} className="text-white ml-0.5 fill-white" />
                      </div>
                    </div>
                    
                    {/* Reel indicator */}
                    <div className="absolute bottom-2 sm:bottom-3 left-2 sm:left-3 right-2 sm:right-3 flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full overflow-hidden border border-white/30">
                          <img src={LOGO_URL} alt="Nammareels" className="w-full h-full object-cover" />
                        </div>
                        <span className="text-white text-[9px] sm:text-[10px] font-body">nammareels.in</span>
                      </div>
                      <div className="flex items-center gap-1 text-white/70">
                        <Smartphone size={10} />
                        <span className="text-[9px] sm:text-[10px] font-body">Reel</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Phone Buttons */}
            <div className="absolute right-[-2px] top-[85px] sm:top-[98px] md:top-[110px] w-[3px] h-[42px] sm:h-[48px] md:h-[55px] bg-gradient-to-b from-[#2a2a2a] to-[#1a1a1a] rounded-r-sm" />
            <div className="absolute left-[-2px] top-[72px] sm:top-[82px] md:top-[92px] w-[3px] h-[20px] sm:h-[24px] md:h-[28px] bg-gradient-to-b from-[#2a2a2a] to-[#1a1a1a] rounded-l-sm" />
            <div className="absolute left-[-2px] top-[97px] sm:top-[112px] md:top-[126px] w-[3px] h-[34px] sm:h-[40px] md:h-[46px] bg-gradient-to-b from-[#2a2a2a] to-[#1a1a1a] rounded-l-sm" />
          </div>
        </div>

        {/* BOTTOM: Book Now Button - Centered, Below Phone */}
        <div className="animate-slide-up stagger-3">
          <Button
            data-testid="hero-book-now-btn"
            onClick={onBookNow}
            className="bg-[#dc2626] hover:bg-[#b91c1c] text-white font-heading uppercase tracking-wider px-10 md:px-12 py-5 md:py-6 text-base md:text-lg transition-all duration-300 ease-out hover:scale-[1.02] hover:shadow-[0_10px_40px_rgba(220,38,38,0.4)]"
          >
            Book Now
            <ChevronRight size={20} />
          </Button>
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUsSection = () => {
  const features = [
    {
      icon: <Zap className="text-[#dc2626]" size={32} />,
      title: "Same Day Delivery",
      description: "Shoot in the morning, get your edited reel by evening. Fast turnaround guaranteed."
    },
    {
      icon: <Camera className="text-[#dc2626]" size={32} />,
      title: "Professional Gear",
      description: "Latest iPhones and Sony Alpha cameras with gimbal stabilization for cinematic shots."
    },
    {
      icon: <Film className="text-[#dc2626]" size={32} />,
      title: "Expert Editing",
      description: "Professional color grading, transitions, and music sync optimized for viral reach."
    },
    {
      icon: <Sparkles className="text-[#dc2626]" size={32} />,
      title: "Premium Quality",
      description: "Cinematic quality content that stands out on Instagram and YouTube."
    },
    {
      icon: <Clock className="text-[#dc2626]" size={32} />,
      title: "Flexible Scheduling",
      description: "Book a slot that works for you. Weekend and evening slots available."
    },
    {
      icon: <CheckCircle className="text-[#dc2626]" size={32} />,
      title: "Satisfaction Guaranteed",
      description: "Most of our clients are happy with the results we deliver."
    }
  ];

  return (
    <section id="why-us" data-testid="why-us-section" className="py-24 md:py-32 relative">
      <div className="container mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-[#dc2626] font-heading text-sm uppercase tracking-[0.3em] mb-4">
            Why Choose Us
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase">
            Not Just Videos, <span className="text-[#dc2626]">Viral Content</span>
          </h2>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div 
              key={index}
              data-testid={`feature-card-${index}`}
              className="bg-[#0a0a0a] border border-white/10 rounded-xl p-8 hover:border-[#dc2626]/50 transition-all duration-300 pricing-card"
            >
              <div className="mb-4">{feature.icon}</div>
              <h3 className="font-heading text-xl uppercase text-white mb-3">{feature.title}</h3>
              <p className="font-body text-white/60">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      title: "Book Your Slot",
      description: "Choose your package, pick a date, and fill the enquiry form. We'll confirm within hours.",
      icon: <Phone className="text-[#dc2626]" size={28} />
    },
    {
      number: "02",
      title: "We Shoot",
      description: "Our professional videographer arrives at your location with all equipment ready.",
      icon: <Video className="text-[#dc2626]" size={28} />
    },
    {
      number: "03",
      title: "Edit & Deliver",
      description: "We edit, color grade, and deliver your polished reel the same day. Post and go viral!",
      icon: <Play className="text-[#dc2626]" size={28} />
    }
  ];

  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="py-24 md:py-32 bg-[#0a0a0a] relative">
      <div className="container mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-[#dc2626] font-heading text-sm uppercase tracking-[0.3em] mb-4">
            How It Works
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase">
            Simple <span className="text-[#dc2626]">3-Step</span> Process
          </h2>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-8 md:gap-4 relative">
          {/* Connecting Line (Desktop) */}
          <div className="hidden md:block absolute top-24 left-[20%] right-[20%] h-[2px] bg-gradient-to-r from-[#dc2626]/50 via-[#dc2626] to-[#dc2626]/50" />

          {steps.map((step, index) => (
            <div 
              key={index}
              data-testid={`step-${index + 1}`}
              className="relative text-center"
            >
              {/* Step Number Circle */}
              <div className="relative z-10 mx-auto w-20 h-20 rounded-full bg-[#dc2626] flex items-center justify-center mb-6">
                <span className="font-heading text-2xl font-bold text-white">{step.number}</span>
              </div>

              {/* Content */}
              <div className="bg-[#050505] border border-white/10 rounded-xl p-6 mt-4">
                <div className="flex justify-center mb-4">{step.icon}</div>
                <h3 className="font-heading text-xl uppercase text-white mb-3">{step.title}</h3>
                <p className="font-body text-white/60 text-sm">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = ({ onBookNow }) => {
  const plans = [
    {
      name: "Basic Reel Plan",
      price: "₹1,499",
      popular: false,
      features: [
        "iPhone Shoot",
        "1 Hour Shoot",
        "30 Seconds Final Edited Video",
        "Professional Editing",
        "Instagram Reel Format (Vertical)",
        "Edited Footage Included"
      ]
    },
    {
      name: "Creator Half-Day Plan",
      price: "₹3,999",
      popular: true,
      features: [
        "iPhone Shoot",
        "3 Hours Shooting Time",
        "2 Edited Reels",
        "Multiple Concepts Coverage",
        "Professional Editing",
        "Social Media Ready Format"
      ]
    },
    {
      name: "Pro Camera Plan",
      price: "₹4,999",
      popular: false,
      features: [
        "Sony Alpha Camera Shoot",
        "Stabilized Gimbal Setup",
        "2 Hours Shooting Time",
        "1 Premium Edited Reel",
        "Cinematic Color Grading",
        "High Quality Output"
      ]
    },
    {
      name: "Wedding Special Plan",
      price: "₹13,000",
      popular: false,
      featured: true,
      features: [
        "Professional Camera + iPhone Shoot",
        "Wedding Function Coverage",
        "Cinematic Video Shoot",
        "Wedding Highlight Reel",
        "Professional Editing & Color Grading",
        "Final HD Delivery"
      ]
    }
  ];

  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 md:py-32 relative">
      <div className="container mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-[#dc2626] font-heading text-sm uppercase tracking-[0.3em] mb-4">
            Pricing Plans
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase">
            Choose Your <span className="text-[#dc2626]">Perfect Plan</span>
          </h2>
          <p className="font-body text-white/60 mt-4 max-w-2xl mx-auto">
            Professional content creation services tailored for creators, brands, and special occasions
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan, index) => (
            <div 
              key={index}
              data-testid={`pricing-card-${index}`}
              className={`relative rounded-xl p-6 pricing-card ${
                plan.featured 
                  ? "gradient-border bg-[#0a0a0a]" 
                  : plan.popular 
                    ? "bg-[#0a0a0a] border-2 border-[#dc2626]" 
                    : "bg-[#0a0a0a] border border-white/10"
              }`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#dc2626] text-white text-xs font-heading uppercase tracking-wider px-4 py-1 rounded-full">
                  Most Popular
                </div>
              )}

              {/* Featured Badge */}
              {plan.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#dc2626] to-[#ef4444] text-white text-xs font-heading uppercase tracking-wider px-4 py-1 rounded-full animate-glow-pulse">
                  Premium
                </div>
              )}

              {/* Plan Name */}
              <h3 className="font-heading text-lg uppercase text-white mb-2 mt-2">{plan.name}</h3>

              {/* Price */}
              <div className="mb-6">
                <span className="font-heading text-4xl md:text-5xl font-bold text-[#dc2626]">{plan.price}</span>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, fIndex) => (
                  <li key={fIndex} className="flex items-start gap-3 text-white/70 font-body text-sm">
                    <CheckCircle size={18} className="text-[#dc2626] flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <Button
                data-testid={`pricing-book-btn-${index}`}
                onClick={onBookNow}
                className={`w-full font-heading uppercase tracking-wider py-5 ${
                  plan.featured || plan.popular
                    ? "bg-[#dc2626] hover:bg-[#b91c1c] text-white"
                    : "bg-white/10 hover:bg-white/20 text-white"
                } btn-primary`}
              >
                Book Now
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "Priya Sharma",
      role: "Content Creator",
      text: "Nammareels delivered exactly what I needed. The quality was amazing and turnaround was super fast!",
      rating: 5
    },
    {
      name: "Rahul Menon",
      role: "Restaurant Owner",
      text: "Got reels for my cafe and they went viral! Professional team and great understanding of what works on Instagram.",
      rating: 5
    },
    {
      name: "Sneha & Arun",
      role: "Wedding Clients",
      text: "Our wedding reel was stunning! They captured all the special moments beautifully. Highly recommend!",
      rating: 5
    },
    {
      name: "Vikram Reddy",
      role: "Fitness Influencer",
      text: "Best reel makers in Bengaluru! They understand trends and create content that actually performs.",
      rating: 5
    },
    {
      name: "Anjali Nair",
      role: "Fashion Blogger",
      text: "Super impressed with the cinematic quality. Same day delivery saved my content calendar!",
      rating: 5
    },
    {
      name: "Karthik Kumar",
      role: "Startup Founder",
      text: "Used them for our product launch video. Clean, professional, and delivered on time.",
      rating: 5
    }
  ];

  // Double the testimonials for infinite scroll effect
  const doubledTestimonials = [...testimonials, ...testimonials];

  return (
    <section data-testid="testimonials-section" className="py-24 md:py-32 bg-[#0a0a0a] overflow-hidden">
      <div className="container mx-auto px-4 md:px-8 mb-12">
        <div className="text-center">
          <p className="text-[#dc2626] font-heading text-sm uppercase tracking-[0.3em] mb-4">
            Testimonials
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase">
            What Our <span className="text-[#dc2626]">Clients Say</span>
          </h2>
        </div>
      </div>

      {/* Marquee Container */}
      <div className="marquee-container">
        <div className="marquee-content">
          {doubledTestimonials.map((testimonial, index) => (
            <div 
              key={index}
              data-testid={`testimonial-${index}`}
              className="flex-shrink-0 w-[350px] mx-4 bg-[#050505] border border-white/10 rounded-xl p-6"
            >
              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} size={16} className="fill-[#dc2626] text-[#dc2626]" />
                ))}
              </div>

              {/* Quote */}
              <p className="font-body text-white/80 mb-6 italic">"{testimonial.text}"</p>

              {/* Author */}
              <div>
                <p className="font-heading text-white uppercase">{testimonial.name}</p>
                <p className="font-body text-white/50 text-sm">{testimonial.role}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const faqs = [
    {
      question: "What areas do you cover in Bengaluru?",
      answer: "We cover all of Bengaluru and nearby areas including Electronic City, Whitefield, Koramangala, Indiranagar, HSR Layout, and more. Travel charges may apply for locations beyond 15km from our base."
    },
    {
      question: "How fast is the delivery?",
      answer: "We deliver your edited reel on the same day of the shoot. For basic packages, expect delivery within 4-6 hours. Wedding and premium packages may take up to 24 hours for the final polished output."
    },
    {
      question: "What equipment do you use?",
      answer: "We use the latest iPhone Pro models for most shoots, ensuring smooth 4K footage. For Pro Camera packages, we use Sony Alpha cameras with DJI gimbals for cinematic stabilization."
    },
    {
      question: "Can I request revisions?",
      answer: "Yes! All our packages include up to 2 rounds of revisions. We want you to be 100% satisfied with the final output. Additional revisions can be requested at a nominal fee."
    },
    {
      question: "What's included in the Wedding Special package?",
      answer: "The Wedding Special includes full function coverage, both camera and iPhone shots, a highlight reel (2-3 minutes), professional color grading, and licensed music. We capture all key moments from rituals to celebrations."
    },
    {
      question: "How do I book?",
      answer: "Simply click the 'Book Now' button, fill out the enquiry form with your details and preferred date. Our team will contact you within a few hours to confirm your booking and discuss requirements."
    },
    {
      question: "What's your cancellation policy?",
      answer: "Cancellations made 48 hours before the shoot are fully refundable. Cancellations within 48 hours may incur a 25% fee. Rescheduling is always free if done 24 hours in advance."
    }
  ];

  return (
    <section id="faq" data-testid="faq-section" className="py-24 md:py-32 relative">
      <div className="container mx-auto px-4 md:px-8 max-w-3xl">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-[#dc2626] font-heading text-sm uppercase tracking-[0.3em] mb-4">
            FAQ
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase">
            Got <span className="text-[#dc2626]">Questions?</span>
          </h2>
        </div>

        {/* Accordion */}
        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, index) => (
            <AccordionItem 
              key={index} 
              value={`item-${index}`}
              data-testid={`faq-item-${index}`}
              className="bg-[#0a0a0a] border border-white/10 rounded-xl px-6 overflow-hidden"
            >
              <AccordionTrigger className="font-heading text-white text-left uppercase text-base md:text-lg hover:text-[#dc2626] hover:no-underline py-5">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="font-body text-white/70 pb-5">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

// Footer Section
const Footer = ({ onBookNow }) => {
  return (
    <footer data-testid="footer" className="bg-[#050505] border-t border-white/10 py-16">
      <div className="container mx-auto px-4 md:px-8">
        <div className="grid md:grid-cols-4 gap-12">
          {/* Logo & Description */}
          <div className="md:col-span-2">
            <img 
              src={LOGO_URL} 
              alt="Nammareels.in" 
              className="h-12 w-auto mb-6"
            />
            <p className="font-body text-white/60 max-w-sm mb-6">
              Bengaluru's premier quick content service. We shoot, edit, and deliver professional reels the same day.
            </p>
            {/* Social Links */}
            <div className="flex gap-4">
              <a 
                href="https://www.instagram.com/nammareels.in_?igsh=NjA4Y2s2ZnFscmdp" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#dc2626] transition-colors"
              >
                <Instagram size={20} />
              </a>
              <a 
                href="https://youtube.com/@nammareels" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="footer-youtube"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#dc2626] transition-colors"
              >
                <Youtube size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-white uppercase mb-6">Quick Links</h4>
            <ul className="space-y-3">
              <li><a href="#why-us" className="font-body text-white/60 hover:text-white transition-colors">Why Us</a></li>
              <li><a href="#how-it-works" className="font-body text-white/60 hover:text-white transition-colors">How It Works</a></li>
              <li><a href="#pricing" className="font-body text-white/60 hover:text-white transition-colors">Pricing</a></li>
              <li><a href="#faq" className="font-body text-white/60 hover:text-white transition-colors">FAQ</a></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading text-white uppercase mb-6">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-white/60 font-body">
                <MapPin size={18} className="text-[#dc2626]" />
                <span>Bengaluru, Karnataka</span>
              </li>
              <li>
                <a href="mailto:nammareels.in@gmail.com" className="flex items-center gap-3 text-white/60 font-body hover:text-white transition-colors">
                  <Mail size={18} className="text-[#dc2626]" />
                  <span>nammareels.in@gmail.com</span>
                </a>
              </li>
              <li>
                <a href="tel:+916366910844" className="flex items-center gap-3 text-white/60 font-body hover:text-white transition-colors">
                  <Phone size={18} className="text-[#dc2626]" />
                  <span>+91 6366910844</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-body text-white/40 text-sm">
            © {new Date().getFullYear()} Nammareels.in. All rights reserved.
          </p>
          <Button
            data-testid="footer-book-now-btn"
            onClick={onBookNow}
            className="bg-[#dc2626] hover:bg-[#b91c1c] text-white font-heading uppercase tracking-wider px-6"
          >
            Book Now
          </Button>
        </div>
      </div>
    </footer>
  );
};

// Booking Modal Component
const BookingModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    date: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  // Map service keys to actual plan names
  const SERVICE_MAP = {
    "basic": "Basic Reel Plan",
    "halfday": "Creator Half-Day Plan",
    "pro": "Pro Camera Plan",
    "wedding": "Wedding Special Plan"
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      // Build request body matching backend schema
      const requestBody = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email || null,
        planName: SERVICE_MAP[formData.service] || formData.service,
        eventDate: formData.date || null,
        message: formData.message || null,
        city: "Bengaluru"
      };

      const response = await axios.post(`${API}/bookings`, requestBody, {
        headers: { "Content-Type": "application/json" }
      });

      // Check for successful response (200 or 201)
      if (response.status === 200 || response.status === 201) {
        setSubmitted(true);
        toast.success("Booking received! We'll contact you shortly.");
      } else {
        throw new Error("Unexpected response");
      }
    } catch (err) {
      console.error("Error submitting booking:", err);
      const errorMessage = err.response?.data?.detail || "Failed to submit booking. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
    setError("");
    setFormData({ name: "", phone: "", email: "", service: "", date: "", message: "" });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        data-testid="booking-modal"
        className="bg-[#0a0a0a] border border-white/10 text-white max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl uppercase text-white">
            {submitted ? "Thank You!" : "Book Your Shoot"}
          </DialogTitle>
          <DialogDescription className="font-body text-white/60">
            {submitted 
              ? "We've received your enquiry and will contact you shortly."
              : "Fill in your details and we'll get back to you within a few hours."
            }
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="py-8 text-center">
            <div className="w-16 h-16 bg-[#dc2626]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-[#dc2626]" />
            </div>
            <p className="font-body text-white/80 mb-6">
              Enquiry received. We'll contact you shortly!
            </p>
            <a 
              href="https://wa.me/91XXXXXXXXXX"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-green-500 hover:text-green-400 font-body"
            >
              <MessageCircle size={20} />
              Or chat with us on WhatsApp
            </a>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm font-body">
                {error}
              </div>
            )}
            <div>
              <Label htmlFor="name" className="font-body text-white/80">Name *</Label>
              <Input
                id="name"
                data-testid="booking-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="bg-white/5 border-white/20 text-white mt-1"
                placeholder="Your name"
              />
            </div>

            <div>
              <Label htmlFor="phone" className="font-body text-white/80">Phone Number *</Label>
              <Input
                id="phone"
                data-testid="booking-phone-input"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                className="bg-white/5 border-white/20 text-white mt-1"
                placeholder="+91 XXXXXXXXXX"
              />
            </div>

            <div>
              <Label htmlFor="email" className="font-body text-white/80">Email</Label>
              <Input
                id="email"
                data-testid="booking-email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-white/5 border-white/20 text-white mt-1"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <Label htmlFor="service" className="font-body text-white/80">Service *</Label>
              <Select 
                value={formData.service} 
                onValueChange={(value) => setFormData({ ...formData, service: value })}
              >
                <SelectTrigger 
                  data-testid="booking-service-select"
                  className="bg-white/5 border-white/20 text-white mt-1"
                >
                  <SelectValue placeholder="Select a package" />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/20">
                  <SelectItem value="basic" className="text-white">Basic Reel Plan - ₹1,499</SelectItem>
                  <SelectItem value="halfday" className="text-white">Creator Half-Day Plan - ₹3,999</SelectItem>
                  <SelectItem value="pro" className="text-white">Pro Camera Plan - ₹4,999</SelectItem>
                  <SelectItem value="wedding" className="text-white">Wedding Special Plan - ₹13,000</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="date" className="font-body text-white/80">Preferred Date</Label>
              <Input
                id="date"
                data-testid="booking-date-input"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="bg-white/5 border-white/20 text-white mt-1"
              />
            </div>

            <Button
              type="submit"
              data-testid="booking-submit-btn"
              disabled={isSubmitting || !formData.name || !formData.phone || !formData.service}
              className="w-full bg-[#dc2626] hover:bg-[#b91c1c] text-white font-heading uppercase tracking-wider py-5 mt-4"
            >
              {isSubmitting ? "Submitting..." : "Submit Enquiry"}
            </Button>

            <p className="text-center text-white/50 text-sm font-body">
              Or reach us directly on{" "}
              <a 
                href="https://wa.me/91XXXXXXXXXX" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-green-500 hover:text-green-400"
              >
                WhatsApp
              </a>
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

// WhatsApp Floating Button
const WhatsAppButton = () => {
  return (
    <a
      href="https://wa.me/916366910844"
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-float-btn"
      className="whatsapp-float"
    >
      <div className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-shadow">
        <MessageCircle size={28} className="text-white fill-white" />
      </div>
    </a>
  );
};

// Main App Component
function HomePage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  const openBookingModal = () => setIsBookingOpen(true);
  const closeBookingModal = () => setIsBookingOpen(false);
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMenuOpen]);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Toaster position="top-center" richColors />
      
      {/* Navigation */}
      <Navbar 
        onBookNow={openBookingModal} 
        onMenuToggle={toggleMenu}
        isMenuOpen={isMenuOpen}
      />
      
      {/* Mobile Menu */}
      <MobileMenu 
        isOpen={isMenuOpen} 
        onClose={closeMenu} 
        onBookNow={openBookingModal}
      />

      {/* Main Content */}
      <main>
        <HeroSection onBookNow={openBookingModal} />
        <WhyChooseUsSection />
        <HowItWorksSection />
        <PricingSection onBookNow={openBookingModal} />
        <TestimonialsSection />
        <FAQSection />
      </main>

      {/* Footer */}
      <Footer onBookNow={openBookingModal} />

      {/* Booking Modal */}
      <BookingModal isOpen={isBookingOpen} onClose={closeBookingModal} />

      {/* WhatsApp Floating Button */}
      <WhatsAppButton />
    </div>
  );
}

// Protected Admin Route
const ProtectedAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-white font-heading">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <AdminLogin />;
  }
  
  return children;
};

// Main App with Router
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route 
            path="/admin/*" 
            element={
              <ProtectedAdminRoute>
                <AdminDashboard />
              </ProtectedAdminRoute>
            } 
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
